import type { LucideIcon } from 'lucide-react';
import {
  Cable,
  LayoutDashboard,
  BellRing,
  Workflow,
  BarChart3,
  Factory,
  ScanEye,
  Sparkles,
  ShieldCheck,
} from 'lucide-react';

/**
 * Platform modules. Each renders as /platform/<slug> through ModulePage, and
 * feeds the mega menu, the platform overview and the footer.
 *
 * Claims here must be things the product does today — check the build roadmap
 * (training/01-build-roadmap.md) before adding one.
 */

export interface ModuleCapability {
  title: string;
  body: string;
}

export interface PlatformModule {
  slug: string;
  name: string;
  /** Short line for menus and cards. */
  summary: string;
  icon: LucideIcon;
  color: string;
  /** Page hero. */
  headline: string;
  intro: string;
  capabilities: ModuleCapability[];
  /** Chip lists shown under the capabilities, e.g. supported protocols. */
  chipGroups?: Array<{ title: string; chips: string[] }>;
  /**
   * A "works with anything" band shown above the capabilities: the hardware
   * it supports, as logos or icons, and why switching between them is free.
   */
  /** Real screenshots shown in a slider above the capabilities. */
  gallery?: { eyebrow: string; title: string; intro: string; slides: Array<{ label: string; caption: string; src: string }> };
  /** A band of smaller points shown after the capabilities, e.g. the tag system behind the logic. */
  featureBand?: {
    eyebrow: string;
    title: string;
    intro: string;
    items: ModuleCapability[];
  };
  compatibility?: {
    eyebrow: string;
    title: string;
    intro: string;
    points: string[];
    /** `logo` is a path under public/; otherwise `icon` picks a generic one. */
    devices: Array<{ name: string; detail: string; logo?: string; icon?: 'usb' | 'ip' }>;
    footnote?: string;
  };
  /** Ink U course numbers that teach this module. */
  courses: number[];
  /** Which mockup the hero shows. */
  visual: 'dashboard' | 'protocols' | 'alarms' | 'logic' | 'analytics' | 'mes' | 'vision' | 'ai' | 'security';
}

export const PLATFORM_MODULES: PlatformModule[] = [
  {
    slug: 'connectivity',
    name: 'Connectivity',
    summary: '25+ native drivers, a network scanner and outbound publishing.',
    icon: Cable,
    color: '#3b82f6',
    headline: 'Connect every machine on the floor — old, new and in between',
    intro:
      'Native drivers for PLCs, CNCs, robots, cameras and building systems run on the appliance itself. Point the Network Scanner at a subnet and it finds, fingerprints and identifies what is out there. Then publish that data onward over OPC-UA, MQTT Sparkplug B, SQL or REST.',
    capabilities: [
      {
        title: 'Network Scanner',
        body: 'Discovers IPs, probes ports and fingerprints protocols so PLCs, CNCs and sensors identify themselves — maker, model, firmware and serial where the device reports them. No spreadsheet of addresses required.',
      },
      {
        title: 'Native drivers, no gateways',
        body: 'Allen-Bradley, Siemens, Omron, Mitsubishi, GE, Fanuc and more speak directly to the appliance, with tag browsing where the controller supports it, 32-bit and floating-point values, and automatic reconnect when a link drops.',
      },
      {
        title: 'Robots and semiconductor equipment',
        body: 'Universal Robots, KUKA and FANUC robot drivers, plus SECS/GEM for fab tools, alongside the PLC and CNC drivers.',
      },
      {
        title: 'Built-in I/O and serial',
        body: 'Onboard digital and analog inputs, RS-232 and RS-485 become tags like any other device. Barcode scanners and weigh scales connect as auxiliary devices.',
      },
      {
        title: 'Store-and-forward',
        body: 'Historian, remote-database and transaction-group writes are queued through outages and delivered in order when the link returns, with a quarantine for records that repeatedly fail.',
      },
      {
        title: 'Publish outward',
        body: 'An embedded OPC-UA server, MQTT Sparkplug B, remote SQL databases, outbound REST and a versioned REST API with OpenAPI docs.',
      },
    ],
    chipGroups: [
      { title: 'CNC & machine tools', chips: ['MTConnect', 'Fanuc FOCAS'] },
      { title: 'Rockwell', chips: ['ControlLogix', 'CompactLogix', 'SLC 500', 'MicroLogix', 'EtherNet/IP'] },
      { title: 'PLC platforms', chips: ['Siemens S7', 'Omron FINS', 'Mitsubishi MELSEC', 'AutomationDirect', 'GE / Emerson SRTP'] },
      { title: 'Robots & fab', chips: ['Universal Robots', 'KUKA', 'FANUC Robot', 'SECS/GEM'] },
      { title: 'Open standards', chips: ['OPC-UA client', 'OPC-UA server', 'Modbus TCP', 'Modbus RTU', 'MQTT', 'Sparkplug B'] },
      { title: 'Anything else', chips: ['HTTP / REST', 'Custom JSON', 'Custom XML', 'Raw TCP', 'Barcode scanners', 'Weigh scales'] },
    ],
    courses: [3, 4, 21],
    visual: 'protocols',
  },
  {
    slug: 'visualization',
    name: 'Dashboards & HMI',
    summary: 'Drag-and-drop dashboards, templates, custom pages and TV displays.',
    icon: LayoutDashboard,
    color: '#7c3aed',
    headline: 'Screens operators actually use, built in an afternoon',
    intro:
      'A drag-and-drop dashboard builder with a full widget library, reusable machine templates, vector graphics bound to live tags and fully custom HTML pages. Put them on a desktop, a tablet, a kiosk or the TV over the line.',
    capabilities: [
      {
        title: 'Dashboard builder',
        body: 'Drag gauges, multi-tag trends, tables, status, OEE, andon and camera widgets onto the grid, with undo and redo, separate desktop, tablet and phone layouts, and named style classes so a restyle happens once.',
      },
      {
        title: 'Templates & faceplates',
        body: 'Build a machine faceplate once from a UDT, repeat it across every machine, and click through to parameterized pop-up views.',
      },
      {
        title: 'Vector graphics & symbols',
        body: 'Import SVG drawings and bind fill, stroke, visibility, text and rotation of individual elements to tags — a live P&ID without redrawing it.',
      },
      {
        title: 'Custom pages',
        body: 'Full HTML, CSS and JavaScript pages with live widget shortcodes and their own images, stylesheets and fonts, shown in the app or standalone and full screen — or describe the page you want and let the AI page designer draft it.',
      },
      {
        title: 'Power Chart',
        body: 'Pick pens, scale each one, drag to zoom and read every value at a cursor. Save the pen set for the next investigation.',
      },
      {
        title: 'Navigation, forms and displays',
        body: 'Role-aware navigation bars, dashboards limited to the user groups you choose, forms that write to tables or tags, public pages, and kiosk and TV modes for the shop floor.',
      },
    ],
    courses: [7, 8, 9, 19],
    visual: 'dashboard',
  },
  {
    slug: 'alarms',
    name: 'Alarms & Notifications',
    summary: 'Alarm rules, escalation pipelines, SMS and voice, and ISA-18.2 metrics.',
    icon: BellRing,
    color: '#06b6d4',
    headline: 'Catch problems early and reach the right person',
    intro:
      'Alarm rules on any tag, with escalation that reaches the right person by email, SMS or phone call — and the metrics to keep the alarm system itself healthy, so a real fault is never buried under nuisance alarms.',
    capabilities: [
      {
        title: 'Alarm rules on any tag',
        body: 'Thresholds, ranges, conditions and rate of change, with severities, on- and off-delays and a deadband, plus an automatic alarm when a device drops off the network.',
      },
      {
        title: 'Escalation pipelines',
        body: 'Multi-stage escalation with delays between steps, delivered by email, SMS and voice call to user groups or whoever is on call, with quiet hours for non-critical alarms.',
      },
      {
        title: 'SMS and voice calls',
        body: 'Text messages and text-to-speech phone calls through the Scada.Ink Cloud relay, or your own Twilio account and caller ID.',
      },
      {
        title: 'Acknowledge and shelve',
        body: 'Operators acknowledge one alarm or many at once, with a comment, and shelve a known nuisance for a set time, with who and when recorded.',
      },
      {
        title: 'ISA-18.2 alarm metrics',
        body: 'Alarm rates, floods, chattering and bad actors measured against the standard.',
      },
      {
        title: 'Alarm history',
        body: 'Every occurrence recorded, searchable by severity and text, and exportable to CSV for audits and incident reviews.',
      },
    ],
    courses: [6],
    visual: 'alarms',
  },
  {
    slug: 'logic',
    name: 'Logic & Control',
    summary: 'Ladder logic, sequential function charts, visual workflows and JavaScript.',
    icon: Workflow,
    color: '#0d9488',
    headline: 'Four ways to write logic, all running on the appliance',
    intro:
      'Ladder logic for scan-based machine logic, sequential function charts for step-by-step sequences, visual workflows for event-driven actions, and JavaScript for everything that needs real code. They share the same tags, can start one another, and keep running when nobody is logged in.',
    capabilities: [
      {
        title: 'Ladder logic',
        body: 'A soft-PLC with an editor modeled on Allen-Bradley CompactLogix: contacts, coils, timers, counters, math and compare instructions, with live monitoring and bit forcing for commissioning.',
      },
      {
        title: 'Sequential function charts',
        body: 'Draw a sequence as steps and transitions — fill, heat, drain — with parallel and selection branches, step actions including timed qualifiers, and transitions written as ordinary ladder rungs.',
      },
      {
        title: 'Visual workflows',
        body: 'Trigger on tag changes, schedules, device status or logins; branch, run paths in parallel, compute and act — write tags, call APIs, insert rows, send notifications — and watch the path each run took on the canvas.',
      },
      {
        title: 'JavaScript scripting',
        body: 'Scripts and shared libraries in a sandboxed runtime, for parsing, loops, calculations, web services and database work a diagram cannot express.',
      },
      {
        title: 'User-defined types (UDTs)',
        body: 'Define a machine’s tags once — a “Spindle” with Speed, Load and Temp — with parameters that map it onto each machine’s real PLC addresses, then create instances one at a time or dozens at once from a pattern. Every instance is ordinary tags, so logic, alarms and screens built for one work for all.',
      },
      {
        title: 'Works together',
        body: 'A workflow can run a script, a script can start a workflow or a ladder task, and schedules can run any of them on a timetable.',
      },
    ],
    gallery: {
      eyebrow: 'See it',
      title: 'Pick the language that fits the job',
      intro: 'All four editors are built into the appliance’s web interface — nothing to install on an engineering laptop.',
      slides: [
        { label: 'Ladder logic', src: '/screens/logic/ladder.jpg', caption: 'A conveyor seal-in, a settle timer, a good-bottle counter and a fault latch — rungs, instructions and tag bindings in one editor.' },
        { label: 'Sequential function charts', src: '/screens/logic/sfc.jpg', caption: 'A batch sequence: fill, then heat and agitate in parallel, then drain — each step driving its outputs, each transition a ladder condition.' },
        { label: 'Visual workflows', src: '/screens/logic/workflow.jpg', caption: 'At shift change: read the count, email the supervisor if it is under target, then post the result to the MES.' },
        { label: 'JavaScript', src: '/screens/logic/script.jpg', caption: 'A scheduled script that totals the shift, posts it to an MES over HTTP and resets the counters.' },
      ],
    },
    featureBand: {
      eyebrow: 'One tag system',
      title: 'Every language reads and writes the same tags',
      intro:
        'There is one tag system on the appliance, not one per tool. A value is scaled, filtered and quality-stamped once, as it arrives — then ladder, SFC, workflows, scripts, alarms, the historian, dashboards and OPC-UA all see the same thing.',
      items: [
        { title: 'Device tags', body: 'Values from every PLC, CNC, robot and camera, addressed the same way whichever driver they come from.' },
        { title: 'Local tags', body: 'Setpoints, counters and flags that live on the appliance, optionally kept across restarts.' },
        { title: 'Expression tags', body: 'Rates, ratios, states, text and dates computed from other tags — historized and alarmed like any other tag.' },
        { title: 'UDTs', body: 'Structured tag types with parameters, instanced per machine.' },
        { title: 'Scaling', body: 'Linear or square-root scaling from raw counts to engineering units, with units and clamping.' },
        { title: 'Deadband', body: 'Absolute or percent deadband drops noise before it reaches alarms, logic or the historian.' },
        { title: 'Quality and timestamps', body: 'Every value carries good, bad or uncertain quality and the time it changed.' },
        { title: 'History and OPC-UA', body: 'Historize any tag with a checkbox, and expose it to OPC-UA clients — read-only or writable.' },
      ],
    },
    courses: [10, 11, 12, 13],
    visual: 'logic',
  },
  {
    slug: 'analytics',
    name: 'Historian & Analytics',
    summary: 'Historian, OEE, SPC, downtime and scheduled reports.',
    icon: BarChart3,
    color: '#16a34a',
    headline: 'Turn machine signals into the numbers the plant runs on',
    intro:
      'A time-series historian on the appliance, a SQL historian for your own databases, and the production analytics built on top: OEE, downtime Pareto, statistical process control and reports that email themselves.',
    capabilities: [
      {
        title: 'Historian',
        body: 'Per-tag recording and retention with deadbands and engineering-unit scaling, hourly and daily rollups that keep years of trends fast, aggregation and export, plus a public historian API for BI tools.',
      },
      {
        title: 'OEE & downtime',
        body: 'Availability, performance and quality per machine and shift, with downtime reason codes and Pareto analysis.',
      },
      {
        title: 'SPC & quality',
        body: 'Individuals and X-bar/R control charts and capability for critical dimensions, with quality tracking tied back to the machine and the shift.',
      },
      {
        title: 'Report designer',
        body: 'A grid report designer with charts, tables and crosstab pivots, rendered to PDF, HTML or CSV and emailed on a schedule.',
      },
      {
        title: 'Named queries & data tables',
        body: 'Parameterized SQL you write once and reuse in dashboards, reports and scripts; data tables for anything that is not a tag.',
      },
      {
        title: 'Cloud archive',
        body: 'Add Scada.Ink Cloud archiving for an off-site copy with 13-month or 7-year retention.',
      },
    ],
    courses: [5, 14, 15, 16, 17],
    visual: 'analytics',
  },
  {
    slug: 'mes',
    name: 'MES',
    summary: 'Work orders, recipes, work instructions, andon and labor.',
    icon: Factory,
    color: '#ca8a04',
    headline: 'Run the whole operation, not just watch it',
    intro:
      'The MES layer on the same appliance that already knows what every machine is doing: work orders tracked against target, recipes that download to the controller, work instructions with sign-off and andon scoreboards for the floor.',
    capabilities: [
      {
        title: 'Work orders',
        body: 'Queue, run and close orders by part number and priority, with good and reject counts taken straight from machine tags, recorded at the line or posted from your other systems through the API.',
      },
      {
        title: 'Recipes',
        body: 'Parameter sets with min/max limits, downloaded to machines in one step and read back to confirm. Every version is kept to compare and restore, and a part number from the PLC can select the right recipe.',
      },
      {
        title: 'Work instructions',
        body: 'Step-by-step instructions with operator sign-off and a record of who signed and when.',
      },
      {
        title: 'Andon',
        body: 'Full-screen scoreboards for shop-floor TVs: running, idle or down with the downtime reason, live OEE and good and reject counts per machine — no sign-in needed.',
      },
      {
        title: 'Labor efficiency & shifts',
        body: 'Shift calendars and labor tracking against standard, so efficiency is measured the way the plant defines it.',
      },
      {
        title: 'Forms & data entry',
        body: 'Validated forms for checklists, inspections and manual readings, stored alongside the machine data.',
      },
    ],
    courses: [17, 18, 19],
    visual: 'mes',
  },
  {
    slug: 'vision',
    name: 'Machine Vision',
    summary: 'Inspection tools, barcode reading, robot guidance and a failure archive.',
    icon: ScanEye,
    color: '#dc2626',
    headline: 'Inspection results as tags, next to the machine data',
    intro:
      'Machine vision runs as a driver, so a count, a measurement or a pass/fail verdict is just another tag — alarmed, historized and charted like everything else. Calibrate a camera once and every part it finds also has a position in millimeters, and in your robot’s own coordinates. Use the camera you have or one of the supported smart cameras.',
    capabilities: [
      {
        title: 'A full inspection toolbox',
        body: 'Circles, blobs, presence, color match, pattern match, caliper, circle gauge, printed-text verification and barcode, QR and DataMatrix reading.',
      },
      {
        title: 'Several tools, one verdict',
        body: 'Combine tools in one inspection, each with its own pass rule, rolled up into a single device verdict.',
      },
      {
        title: 'Real coordinates, not pixels',
        body: 'Lay a printed ChArUco board under the camera and it is calibrated in seconds — perspective corrected, accuracy shown per point. Regions follow the part, gauges read in millimeters, and every part found has an X and Y from your datum.',
      },
      {
        title: 'Robot guidance',
        body: 'Touch off three points with the robot and positions and part angle are published in its own frame — a pick point as a tag, ready for the PLC or robot controller. No separate vision PC.',
      },
      {
        title: 'Failure image archive',
        body: 'Every failed part’s image is kept for review, and the camera widget shows the latest capture on any dashboard.',
      },
      {
        title: 'Your cameras, your trigger',
        body: 'SICK, Keyence and Cognex presets or any USB, RTSP or HTTP camera. Inspect continuously, or on a trigger tag from the PLC.',
      },
    ],
    compatibility: {
      eyebrow: 'Device agnostic',
      title: 'One set of tools, whatever camera you have',
      intro:
        'Scada.Ink takes the picture from the camera and runs every inspection on the appliance. The camera is just a source of images — so the tools, the calibration, the tags and the training are the same on a Keyence as on a $40 USB webcam.',
      points: [
        'Learn the tools once. Counting, gauging, pattern match, barcodes and robot guidance are set up the same way on every camera.',
        'Change camera brand without rebuilding the inspection — pick the new camera, recalibrate, and the tools and tags carry over.',
        'Mix brands on one station: several cameras, each looking at a different view, rolled up into one verdict.',
        'Use the smart cameras already on the line, or a low-cost USB or IP camera for a new station.',
      ],
      devices: [
        { name: 'Keyence', detail: 'Image over HTTP', logo: '/logos/keyence.svg' },
        { name: 'Cognex', detail: 'In-Sight, image over HTTP', logo: '/logos/cognex.svg' },
        { name: 'SICK', detail: 'PIM60 and HTTP snapshot', logo: '/logos/sick.svg' },
        { name: 'USB cameras', detail: 'Plugged into the appliance', icon: 'usb' },
        { name: 'IP cameras', detail: 'RTSP, MJPEG or HTTP snapshot', icon: 'ip' },
      ],
      footnote: 'Keyence, Cognex and SICK are trademarks of their respective owners. Scada.Ink uses the camera’s image; it does not run the camera’s own on-board tools.',
    },
    courses: [3],
    visual: 'vision',
  },
  {
    slug: 'smart-assist',
    name: 'Smart Assist AI',
    summary: 'AI tag classification, page design and observation analysis.',
    icon: Sparkles,
    color: '#8b5cf6',
    headline: 'AI that does the commissioning grunt work',
    intro:
      'Smart Assist reads a device’s tags, works out what each one is, suggests alarms and deadbands from how the data actually behaves, and designs custom pages from a description. It works even on an air-gapped appliance — the request is relayed through the engineer’s browser, never the plant network.',
    capabilities: [
      {
        title: 'Tag classification',
        body: 'Point it at a scanned device and it classifies hundreds of tags — speed, count, temperature, alarm, state — and builds a dashboard from them.',
      },
      {
        title: 'Observation analysis',
        body: 'A multi-stage triage, classify and verify pipeline turns statistical fingerprints of real data into suggested alarm limits and deadbands.',
      },
      {
        title: 'AI page designer',
        body: 'Describe the screen; get an editable custom page bound to tags that really exist on the appliance.',
      },
      {
        title: 'Air-gap friendly',
        body: 'The appliance never needs internet access. The browser that can reach both carries the request.',
      },
      {
        title: 'You stay in control',
        body: 'Every suggestion is reviewed before it becomes configuration, and nothing is sent that you did not ask it to analyze.',
      },
    ],
    courses: [22],
    visual: 'ai',
  },
  {
    slug: 'security',
    name: 'Security & Administration',
    summary: 'MFA, HTTPS, security levels, audit and API tokens.',
    icon: ShieldCheck,
    color: '#0f766e',
    headline: 'Passes the IT security review',
    intro:
      'Scada.Ink keeps data on your network by design and adds the controls a procurement review asks for: HTTPS with your own certificates, multi-factor sign-in, hierarchical security levels and a field-level audit trail.',
    capabilities: [
      {
        title: 'HTTPS your way',
        body: 'Self-signed out of the box, a CSR signed by your own CA, or automatic certificates through ACME DNS validation.',
      },
      {
        title: 'Multi-factor sign-in & password policy',
        body: 'TOTP authenticator apps with administrator-assisted lockout recovery, plus password rules, expiry, account lockout and idle timeouts per role.',
      },
      {
        title: 'Security levels & tag permissions',
        body: 'Hierarchical security levels plus per-tag write and edit permissions, enforced on the server.',
      },
      {
        title: 'Audit trail',
        body: 'Enhanced audit with field-level change tracking for configuration and operator actions, and electronic signatures — password and optional reason — for recipe downloads, work order completion and SPC limit changes.',
      },
      {
        title: 'API tokens & secrets',
        body: 'Scoped, rate-limited API tokens and an encrypted secrets store for the credentials integrations need.',
      },
      {
        title: 'Four languages',
        body: 'The interface ships in English, Spanish, French and German.',
      },
    ],
    courses: [20, 23, 24],
    visual: 'security',
  },
];

/**
 * Help Center articles for each module, as `category/article` paths on
 * docs.scada.ink. They come from the help site's own URLs — check one exists
 * (apps/docs/.vitepress/dist after a build) before adding it.
 */
export const MODULE_DOCS: Record<string, Array<{ label: string; path: string }>> = {
  connectivity: [
    { label: 'Adding a device', path: 'devices-and-tags/device-add' },
    { label: 'Network Scanner', path: 'devices-and-tags/network-scanner' },
    { label: 'Allen-Bradley PLCs', path: 'devices-and-tags/device-allen-bradley' },
    { label: 'OPC-UA server', path: 'integrations/opcua-server' },
    { label: 'MQTT Sparkplug B', path: 'integrations/sparkplug' },
  ],
  visualization: [
    { label: 'Dashboards', path: 'dashboards/dashboards' },
    { label: 'Widget reference', path: 'dashboards/dashboards-widgets' },
    { label: 'Templates', path: 'templates/templates' },
    { label: 'Custom pages', path: 'scripts-and-custom-pages/custom-pages' },
  ],
  alarms: [
    { label: 'Alarm rules', path: 'alarms-and-history/alarms' },
    { label: 'Alarm pipelines', path: 'alarms-and-history/alarms-pipelines' },
    { label: 'Email, SMS and voice', path: 'alarms-and-history/alarms-notifications' },
    { label: 'ISA-18.2 metrics', path: 'alarms-and-history/alarms-isa-metrics' },
  ],
  logic: [
    { label: 'Ladder logic', path: 'ladder-logic/ladder-overview' },
    { label: 'Sequential function charts', path: 'ladder-logic/ladder-sfc' },
    { label: 'Workflows', path: 'workflows/workflows' },
    { label: 'Scripts', path: 'scripts-and-custom-pages/scripts' },
    { label: 'Tag UDTs', path: 'devices-and-tags/tags-udts' },
    { label: 'Scaling & deadband', path: 'devices-and-tags/tags-scaling-deadband' },
  ],
  analytics: [
    { label: 'Historian', path: 'alarms-and-history/historian' },
    { label: 'OEE setup', path: 'production/oee-setup' },
    { label: 'SPC', path: 'production/spc' },
    { label: 'Reports', path: 'reports-and-data/reports' },
  ],
  mes: [
    { label: 'MES overview', path: 'mes/mes-overview' },
    { label: 'Work orders', path: 'mes/mes-work-orders' },
    { label: 'Recipes', path: 'mes/mes-recipes' },
    { label: 'Work instructions', path: 'mes/mes-work-instructions' },
  ],
  vision: [
    { label: 'Machine vision', path: 'machine-vision/vision' },
    { label: 'Cameras', path: 'machine-vision/vision-cameras' },
    { label: 'Inspection tools', path: 'machine-vision/vision-tools' },
    { label: 'Coordinates & robots', path: 'machine-vision/vision-coordinates' },
    { label: 'Vision tags', path: 'machine-vision/vision-tags' },
  ],
  'smart-assist': [
    { label: 'Smart Assist', path: 'smart-assist/smart-assist' },
    { label: 'Observation analysis', path: 'smart-assist/smart-assist-observations' },
    { label: 'AI page designer', path: 'smart-assist/smart-assist-page-designer' },
    { label: 'Air-gapped appliances', path: 'smart-assist/smart-assist-cloud-relay' },
  ],
  security: [
    { label: 'Users', path: 'system/users' },
    { label: 'Two-factor authentication', path: 'system/system-two-factor' },
    { label: 'Security levels', path: 'system/system-security-levels' },
    { label: 'Audit log', path: 'system/system-audit-log' },
    { label: 'API tokens', path: 'integrations/api-tokens' },
  ],
};

/** Slugs that moved, so old links still land on the right page. */
export const MODULE_SLUG_ALIASES: Record<string, string> = { automation: 'alarms' };

export function findModule(slug: string | undefined): PlatformModule | undefined {
  return PLATFORM_MODULES.find((m) => m.slug === slug);
}
